import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import { getBlogPosts, getBlogPost } from '../utils/blogService';
import LoadingProgress from '../components/LoadingProgress';

marked.setOptions({ breaks: true, gfm: true });

function fmtDate(d) {
  if (!d) return '';
  const parsed = new Date(d + (d.length === 10 ? 'T00:00:00' : ''));
  if (isNaN(parsed)) return d;
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// Read the article slug (if any) out of the URL hash: "#blog/<slug>" -> slug.
function slugFromHash() {
  const m = window.location.hash.match(/^#blog\/(.+)$/);
  return m ? decodeURIComponent(m[1]) : null;
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--color-bg)', color: 'var(--color-text)' },
  publicBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 24px', background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 12 },
  brandMark: {
    width: 36, height: 36, background: 'linear-gradient(135deg,rgba(26,82,118,0.3),rgba(46,134,193,0.1))',
    borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
  },
  brandText: { fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700, letterSpacing: 2, color: 'var(--color-secondary)' },
  signIn: {
    padding: '8px 20px', background: 'linear-gradient(135deg,#1a5276,#2e86c1)', border: 'none',
    borderRadius: 8, color: '#fff', fontFamily: 'var(--font-heading)', fontSize: 11, fontWeight: 700,
    letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
  },
  container: { maxWidth: 1100, margin: '0 auto', padding: '32px 24px' },
  pageTitle: { fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 700, color: 'var(--color-secondary)', letterSpacing: 2, marginBottom: 6 },
  pageSub: { fontSize: 13, color: 'var(--color-text-dim)', marginBottom: 24 },
  controls: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 },
  search: {
    flex: '1 1 240px', minWidth: 200, padding: '10px 14px', background: 'var(--color-surface)',
    border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-text)',
    fontFamily: 'var(--font-body)', fontSize: 14, outline: 'none',
  },
  tagRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 },
  chip: (active) => ({
    padding: '5px 14px', borderRadius: 14, fontSize: 12, fontFamily: 'var(--font-body)', fontWeight: 600, cursor: 'pointer',
    background: active ? 'var(--color-primary)' : 'var(--color-surface)',
    border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
    color: active ? '#fff' : 'var(--color-text-dim)',
  }),
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 },
  tile: {
    background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12,
    overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column', transition: 'border-color 0.15s, transform 0.15s',
  },
  tileImg: { width: '100%', height: 170, objectFit: 'cover', background: 'var(--color-bg)', display: 'block' },
  tileImgFallback: {
    width: '100%', height: 170, background: 'linear-gradient(135deg,rgba(26,82,118,0.25),rgba(46,134,193,0.08))',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38,
  },
  tileBody: { padding: 16, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 },
  tileDate: { fontSize: 11, color: 'var(--color-text-dim)', fontFamily: 'var(--font-data)', textTransform: 'uppercase', letterSpacing: 1 },
  tileTitle: { fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: 'var(--color-text)', lineHeight: 1.3 },
  tileSummary: { fontSize: 13, color: 'var(--color-text-dim)', lineHeight: 1.5, flex: 1 },
  tileTags: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 },
  tilePill: { fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'rgba(46,134,193,0.12)', color: 'var(--color-accent)', fontWeight: 600 },
  backBtn: {
    padding: '6px 14px', border: '1px solid var(--color-border)', borderRadius: 6,
    background: 'transparent', color: 'var(--color-text-dim)', fontSize: 13, cursor: 'pointer', marginBottom: 20,
  },
  articleHero: { width: '100%', maxHeight: 380, objectFit: 'cover', borderRadius: 12, marginBottom: 24, display: 'block' },
  articleTitle: { fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 900, color: 'var(--color-text)', lineHeight: 1.2, marginBottom: 10 },
  articleMeta: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', color: 'var(--color-text-dim)', fontSize: 13, marginBottom: 28 },
  empty: { textAlign: 'center', padding: 64, color: 'var(--color-text-dim)' },
  footer: { textAlign: 'center', padding: 32, borderTop: '1px solid var(--color-border)', marginTop: 48 },
};

// Scoped styling for rendered markdown (injected once).
const ARTICLE_CSS = `
.rv-article { font-family: var(--font-body); font-size: 16px; line-height: 1.7; color: var(--color-text); }
.rv-article h1,.rv-article h2,.rv-article h3 { font-family: var(--font-heading); color: var(--color-secondary); line-height: 1.3; margin: 1.6em 0 0.6em; }
.rv-article h1 { font-size: 26px; } .rv-article h2 { font-size: 22px; } .rv-article h3 { font-size: 18px; }
.rv-article p { margin: 0 0 1.1em; }
.rv-article a { color: var(--color-accent); text-decoration: underline; }
.rv-article img { max-width: 100%; height: auto; border-radius: 10px; margin: 1.2em 0; display: block; }
.rv-article ul,.rv-article ol { margin: 0 0 1.1em 1.4em; } .rv-article li { margin-bottom: 0.4em; }
.rv-article blockquote { border-left: 3px solid var(--color-accent); margin: 1.2em 0; padding: 0.4em 1em; color: var(--color-text-dim); background: var(--color-surface); border-radius: 0 8px 8px 0; }
.rv-article code { font-family: var(--font-data); font-size: 0.9em; background: var(--color-surface); padding: 2px 6px; border-radius: 4px; }
.rv-article pre { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; padding: 14px; overflow-x: auto; }
.rv-article pre code { background: none; padding: 0; }
.rv-article hr { border: none; border-top: 1px solid var(--color-border); margin: 2em 0; }
.rv-article table { border-collapse: collapse; width: 100%; margin: 1.2em 0; }
.rv-article th,.rv-article td { border: 1px solid var(--color-border); padding: 8px 12px; text-align: left; }
`;

export default function BlogPage({ user, onLogin, onBack, ...navProps }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState(null);
  const [slug, setSlug] = useState(() => slugFromHash());
  const [article, setArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);

  // Inject article CSS once.
  useEffect(() => {
    if (document.getElementById('rv-article-css')) return;
    const el = document.createElement('style');
    el.id = 'rv-article-css';
    el.textContent = ARTICLE_CSS;
    document.head.appendChild(el);
  }, []);

  // Keep selected slug in sync with browser back/forward.
  useEffect(() => {
    const onHash = () => setSlug(slugFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Load the post list (debounced on search).
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      setPosts(await getBlogPosts({ search, tag: activeTag }));
    } catch (err) {
      console.error('Blog list load failed:', err);
      setPosts([]);
    }
    setLoading(false);
  }, [search, activeTag]);

  useEffect(() => {
    const t = setTimeout(loadList, 250);
    return () => clearTimeout(t);
  }, [loadList]);

  // Load the selected article.
  useEffect(() => {
    if (!slug) { setArticle(null); return; }
    let cancelled = false;
    setArticleLoading(true);
    getBlogPost(slug)
      .then((a) => { if (!cancelled) setArticle(a); })
      .catch((err) => { if (!cancelled) { console.error('Blog article load failed:', err); setArticle(null); } })
      .finally(() => { if (!cancelled) setArticleLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const openArticle = (s) => { window.location.hash = `#blog/${encodeURIComponent(s)}`; setSlug(s); window.scrollTo(0, 0); };
  const backToList = () => { window.location.hash = '#blog'; setSlug(null); };

  // All tags across the unfiltered listing — derived for the filter chips.
  const allTags = useMemo(() => {
    const set = new Set();
    posts.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [posts]);

  // Render markdown -> sanitized HTML, rewriting relative image paths to the API.
  const articleHtml = useMemo(() => {
    if (!article?.content) return '';
    const raw = marked.parse(article.content);
    const rewritten = raw.replace(/(src|href)="(\.\/)?images\//g, `$1="/api/blog/posts/${article.slug}/images/`);
    return DOMPurify.sanitize(rewritten);
  }, [article]);

  const topBar = user
    ? <Header {...navProps} user={user} onBack={onBack} showBack={!!onBack} />
    : (
      <div style={styles.publicBar}>
        <div
          style={{ ...styles.brand, cursor: onLogin ? 'pointer' : 'default' }}
          onClick={onLogin}
          title={onLogin ? 'Back to sign in' : undefined}
        >
          <div style={styles.brandMark}>&#128483;</div>
          <span style={styles.brandText}>RACEVOICE</span>
        </div>
        {onLogin && <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}><a href="/#signin" style={{ color: 'var(--color-text)', fontSize: 13 }}>Cloud sign in</a><a href="/#get-racevoice" style={{ background: '#ffb547', color: '#101827', borderRadius: 7, padding: '10px 16px', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>Get the app</a></div>}
      </div>
    );

  return (
    <div style={styles.page}>
      {topBar}
      <div style={styles.container}>
        {slug ? (
          // ── ARTICLE VIEW ──
          <>
            <button style={styles.backBtn} onClick={backToList}>&larr; Back to Blog</button>
            {articleLoading ? (
              <LoadingProgress label="Loading" expectedMs={2000} />
            ) : !article ? (
              <div style={styles.empty}>Article not found.</div>
            ) : (
              <article>
                {article.heroImageUrl && <img src={article.heroImageUrl} alt="" style={styles.articleHero} />}
                <h1 style={styles.articleTitle}>{article.title}</h1>
                <div style={styles.articleMeta}>
                  <span>{fmtDate(article.date)}</span>
                  {article.author && <span>&middot; {article.author}</span>}
                  {article.readingTime && <span>&middot; {article.readingTime} min read</span>}
                  {(article.tags || []).map((t) => <span key={t} style={styles.tilePill}>{t}</span>)}
                </div>
                <div className="rv-article" dangerouslySetInnerHTML={{ __html: articleHtml }} />
              </article>
            )}
          </>
        ) : (
          // ── LIST VIEW ──
          <>
            <div style={styles.pageTitle}>RACEVOICE BLOG</div>
            <div style={styles.pageSub}>News, tips, and deep dives on telemetry and going faster on track.</div>

            <div style={styles.controls}>
              <input
                style={styles.search}
                placeholder="Search articles…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {allTags.length > 0 && (
              <div style={styles.tagRow}>
                <button style={styles.chip(!activeTag)} onClick={() => setActiveTag(null)}>All</button>
                {allTags.map((t) => (
                  <button key={t} style={styles.chip(activeTag === t)} onClick={() => setActiveTag(activeTag === t ? null : t)}>{t}</button>
                ))}
              </div>
            )}

            {loading ? (
              <LoadingProgress label="Loading" expectedMs={2000} />
            ) : posts.length === 0 ? (
              <div style={styles.empty}>{search || activeTag ? 'No articles match your search.' : 'No articles yet — check back soon.'}</div>
            ) : (
              <div style={styles.grid}>
                {posts.map((p) => (
                  <div
                    key={p.slug}
                    style={styles.tile}
                    onClick={() => openArticle(p.slug)}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.transform = 'none'; }}
                  >
                    {p.heroImageUrl
                      ? <img src={p.heroImageUrl} alt="" style={styles.tileImg} />
                      : <div style={styles.tileImgFallback}>&#127937;</div>}
                    <div style={styles.tileBody}>
                      <span style={styles.tileDate}>{fmtDate(p.date)}</span>
                      <span style={styles.tileTitle}>{p.title}</span>
                      <span style={styles.tileSummary}>{p.summary}</span>
                      {(p.tags || []).length > 0 && (
                        <div style={styles.tileTags}>
                          {p.tags.slice(0, 4).map((t) => <span key={t} style={styles.tilePill}>{t}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {!user && <aside style={{ margin: '24px auto 40px', padding: 28, width: 'calc(100% - 40px)', maxWidth: 812, borderRadius: 12, background: '#152238', border: '1px solid #2a3a50', textAlign: 'center' }} aria-label="Try RaceVoice">
        <h2 style={{ fontFamily: 'var(--font-body)', color: '#f5f7fa', fontSize: 26 }}>Make your next lap count.</h2>
        <p style={{ color: '#b8c4d4', margin: '12px 0 20px', lineHeight: 1.7 }}>Hear your corner speeds as you drive. Review your laps when you’re back in the paddock.</p>
        <a href="/#get-racevoice" style={{ display: 'inline-block', background: '#ffb547', color: '#101827', borderRadius: 7, padding: '12px 20px', fontWeight: 700, textDecoration: 'none' }}>Get RaceVoice</a>
        <p style={{ color: '#b8c4d4', fontSize: 12, marginTop: 12 }}>All RaceVoice apps are free to download.</p>
      </aside>}
      <div style={styles.footer}>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 11, letterSpacing: 2, color: 'var(--color-secondary)' }}>RACEVOICE</div>
        <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>The RaceVoice Blog</div>
      </div>
    </div>
  );
}
